import { IonContent, IonHeader, IonPage, IonTitle, IonToolbar, IonButton, IonList, IonItem, IonLabel, IonGrid, IonRow, IonCol, IonModal, IonIcon, IonChip, IonInput, IonButtons, IonCard, IonSearchbar } from '@ionic/react';
import { addCircle, closeCircle } from 'ionicons/icons';
import React, { useEffect, useState } from 'react';
import './PlotFilter.css';
import { getAllSalesActivities, getSalesActivityByActivityTypeId } from '../services/sales.service';
import { getDistricts, getStates, getSubDistricts } from '../services/location.service';
import { getUsersWithRolesSupport } from '../services/user.service';
import SelectItem from './SelectItem';

export interface Filters {
    type?: boolean;
    group?: boolean;
    location?: boolean;
}

interface IProps {
    filter: any;
    filterChangeHandler: Function;
    onDoneHandler: Function;
    onCloseHandler: Function;
    hideFilters?: Filters;
    showFilterNameInModal?: boolean;
}

interface PlanOption {
    id: string;
    description: string;
}

interface Agent {
    farmUserId: string;
    username: string;
    mobile: string;
}

interface ServicedByAgent {
    agentId: string;
    agentName: string;
}

const SalesActivityFilter: React.FC<IProps> = (props) => {
    const [acitivitesOptions, setActivitesOptions] = useState<PlanOption[]>([]);
    const [selectedActivity, setSelectedActivity] = useState<string[]>(props?.filter?.type || []);
    const [selectedGroup, setSelectedGroup] = useState<string[]>(props?.filter?.group || []);
    const { hideFilters } = props;

    // Filter name state
    const [filterName, setFilterName] = useState("");
    const [error, setError] = useState(false);

    // location dropdown options
    const [states, setStates] = useState<any[]>([]);
    const [districts, setDistricts] = useState<any[]>([]);
    const [subDistricts, setSubdistricts] = useState<any[]>([]);

    // selected locations
    const [selectedState, setSelectedState] = useState<string>(props?.filter?.location?.state || '');
    const [selectedDistrict, setSelectedDistrict] = useState<string>(props?.filter?.location?.district || '');
    const [selectedSubDistrict, setSelectedSubDistrict] = useState<string>(props?.filter?.location?.subDistrict || '');

    // Serviced by filter state
    const [agentsList, setAgentsList] = useState<Agent[]>([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [showAgentUserModal, setShowAgentUserModal] = useState(false);
    const [servicedByAgents, setServicedByAgents] = useState<ServicedByAgent[]>([]);
    const [servicedByIds, setServicedByIds] = useState<string[]>(props?.filter?.servicedBy || []);

    // loading states
    const [isStateLoading, setIsStateLoading] = useState<boolean>(true);
    const [isDistrictLoading, setIsDistrictLoading] = useState<boolean>(false);
    const [isSubDistrictLoading, setIsSubDistrictLoading] = useState<boolean>(false);

    // modals
    const [showSelectGroupModal, setShowSelectGroupModal] = useState(false);
    const [showSelectActivityModal, setShowSelectActivityModal] = useState(false);
    const [showSelectStateModal, setShowSelectStateModal] = useState(false);
    const [showSelectDistrictModal, setShowSelectDistrictModal] = useState(false);
    const [showSelectSubDistrictModal, setShowSelectSubDistrictModal] = useState(false);

    // group options
    const groupOptions = [
        { id: 'agronomy', description: 'Agronomy' },
        { id: 'sales', description: 'Sales' },
        { id: 'marketing', description: 'Marketing' },
        { id: 'service', description: 'Service' }
    ];

    useEffect(() => {
        let mounted = true;
        getAllSalesActivities().then((res) => setActivitesOptions(res));
        loadAgents();
        mounted = false;
    }, []);

    // Load agents for serviced by filter
    const loadAgents = async () => {
        try {
            const agents = await getUsersWithRolesSupport();
            setAgentsList(agents);
        } catch (error) {
            console.error('Error loading agents:', error);
        }
    };

    // Initialize serviced by agents from props when agentsList is loaded
    useEffect(() => {
        if (props?.filter?.servicedBy && Array.isArray(props.filter.servicedBy) && agentsList.length > 0) {
            const foundAgents = props.filter.servicedBy
                .map((agentId: string) => {
                    const agent = agentsList.find((agent) => agent.farmUserId === agentId);
                    if (agent) {
                        return {
                            agentId: agent.farmUserId,
                            agentName: agent.username,
                        };
                    }
                    return null;
                })
                .filter((agent: any) => agent !== null);
            setServicedByIds(props.filter.servicedBy);
            setServicedByAgents(foundAgents as ServicedByAgent[]);
        }
    }, [props?.filter?.servicedBy, agentsList]);

    useEffect(() => {
        getStates()
            .then((res: { state: string }[]) => {
                setStates(generateOptions(
                    res.map(obj => obj.state)
                        .filter(state => state && state.trim() !== '')
                        .sort()
                ));
                setIsStateLoading(false);
            })
    }, []);
    


    function generateOptions(option: string[]) {
        return option?.map((opt) => {
            return {
                label: opt,
                value: opt
            };
        });
    }

    function getPayloadForLocation(){
        let payload:any = {};

        if(selectedState.length > 0) {
            payload.state = selectedState;
        }
      
        if (selectedDistrict.length > 0) {
            payload.district = selectedDistrict;
        }
    
        if (selectedSubDistrict.length > 0) {
            payload.subDistrict = selectedSubDistrict;
        }

        return payload;
    }

    async function loadDistricts(state: string) {
        setIsDistrictLoading(true);
        getDistricts(state)
            .then((res: { district: string }[]) => {
                unselectOptions(['district', 'subDistrict', 'area']);
                setDistricts(generateOptions(
                    res.map(obj => obj.district)
                        .filter(district => district && district.trim() !== '')
                        .sort()
                ));
                setIsDistrictLoading(false);
            });
    }

    async function loadSubDistricts(district: string) {
        setIsSubDistrictLoading(true);
        getSubDistricts(selectedState, district)
            .then((res: { subDistrict: string }[]) => {
                unselectOptions(['subDistrict', 'area']);
                setSubdistricts(generateOptions(
                    res.map(obj => obj.subDistrict)
                        .filter(subDistrict => subDistrict && subDistrict.trim() !== '')
                        .sort()
                ));
                setIsSubDistrictLoading(false);
            });
    }



    async function setState(item: any) {
        setSelectedState(item.value);
        loadDistricts(item.value);
        setShowSelectStateModal(false);
    }

    async function setDistrict(item: any) {
        setSelectedDistrict(item.value);
        loadSubDistricts(item.value);
        setShowSelectDistrictModal(false);
    }

    async function setSubDistrict(item: any) {
        setSelectedSubDistrict(item.value);
        setShowSelectSubDistrictModal(false);
    }



    function handleDropdownChange(dropdown: string, selected: string) {
        const value = selected;
        switch (dropdown) {
            case 'state':
                if (value) setState({ value });
                else {
                    unselectOptions(['state', 'district', 'subDistrict']);
                    setDistricts([]);
                    setIsDistrictLoading(false);
                }
                break;
            case 'district':
                if (value) setDistrict({ value });
                else {
                    unselectOptions(['district', 'subDistrict']);
                    setSubdistricts([]);
                    setIsSubDistrictLoading(false);
                }
                break;
            case 'subDistrict':
                if (value) setSubDistrict({ value });
                else {
                    setSelectedSubDistrict('');
                    unselectOptions(['subDistrict']);
                }
                break;
            default:
                return;
        }
    }
    
    function unselectOptions(toUnselect: string[]) { 
        let cnt = 0;
        for (const field of toUnselect) {
            if (field === 'state') {
                setSelectedState('');
                cnt++;
            } else if (field === 'district') {
                setSelectedDistrict('');
                if (cnt++ > 0) setDistricts([]);
            } else if (field === 'subDistrict') {
                setSelectedSubDistrict('');
                if (cnt++ > 0) setSubdistricts([]);
            }
        }
    }

    async function addGroup(item: any) {
        setSelectedGroup([...selectedGroup, item.id]);
        setShowSelectGroupModal(false);
    }

    async function addActivity(item: any) {
        setSelectedActivity([...selectedActivity, item.id]);
        setShowSelectActivityModal(false);
    }

    async function removeGroup(groupId: string) {
        setSelectedGroup(selectedGroup.filter(id => id !== groupId));
    }

    async function removeActivity(activityId: string) {
        setSelectedActivity(selectedActivity.filter(id => id !== activityId));
    }

    // Serviced by agent selection functions
    const handleAgentSelection = (agent: Agent) => {
        const isAgentSelected = servicedByAgents.some(
            (assigned) => assigned.agentId === agent.farmUserId
        );

        if (!isAgentSelected) {
            const newAgent = {
                agentId: agent.farmUserId,
                agentName: agent.username,
            };

            setServicedByAgents((prev) => [...prev, newAgent]);
            setServicedByIds([...servicedByIds, agent.farmUserId]);
        }
    };

    const removeServicedByAgent = (agentId: string) => {
        setServicedByAgents((prev) =>
            prev.filter((agent) => agent.agentId !== agentId)
        );
        setServicedByIds(servicedByIds.filter((id) => id !== agentId));
    };

    async function onDone() {
        try{
            // Validation for saved filters
            if (props.showFilterNameInModal && !filterName.trim()) {
                setError(true);
                return;
            }

            // Create filter criteria object to pass to parent
            const filterCriteria = {
                type: selectedActivity,
                group: selectedGroup,
                location: getPayloadForLocation(),
                servicedBy: servicedByIds,
                ...(props.showFilterNameInModal && { filterName })
            };

            props.filterChangeHandler(Object.assign({}, props?.filter, filterCriteria));
            
            // No need to call getSalesActivityByActivityTypeId anymore
            // The main getSalesActivities API will handle all filtering
            props.onDoneHandler([]);
            
            onClose();
        } catch(err){
            console.log(err);
        }
    }

    function onReset() {
        setSelectedActivity([]);
        setSelectedGroup([]);
        setSelectedState('');
        setSelectedDistrict('');
        setSelectedSubDistrict('');
        setDistricts([]);
        setSubdistricts([]);
        setFilterName('');
        setError(false);
        setServicedByAgents([]);
        setServicedByIds([]);
        setSearchTerm('');
        props.filterChangeHandler({});
        props.onDoneHandler([]);
        onClose();
    }

    function onClose() {
        props.onCloseHandler(false);
    }

    return (
        <IonPage>
            <IonHeader>
                <IonToolbar>
                    <IonTitle>Filter</IonTitle>
                </IonToolbar>
            </IonHeader>
            <IonContent fullscreen>
                <IonList>
                    {/* Filter Name Input */}
                    {props.showFilterNameInModal && (
                        <>
                            <IonItem lines="none">
                                <IonLabel position="stacked" style={{ fontWeight: 'bold' }}>
                                    Filter Name
                                </IonLabel>
                                <IonInput
                                    placeholder="Enter filter name"
                                    clearInput
                                    value={filterName}
                                    onIonChange={(e) => {
                                        setFilterName(e.detail.value!);
                                        if (error) setError(false);
                                    }}
                                    style={{
                                        marginTop: "30px",
                                        padding: "5px 10px",
                                        borderRadius: "8px",
                                        backgroundColor: "transparent",
                                        border: "1px solid #dcdcdc",
                                    }}
                                    onFocus={(e) => {
                                        e.target.style.borderColor = "#3880ff";
                                    }}
                                    onBlur={(e) => {
                                        e.target.style.borderColor = "#dcdcdc";
                                    }}
                                />
                                {error && (
                                    <span
                                        style={{
                                            color: "red",
                                            fontSize: "14px",
                                        }}
                                    >
                                        *Proper filter name required
                                    </span>
                                )}
                            </IonItem>
                            <IonItem lines="none"></IonItem>
                        </>
                    )}
                    {
                        !hideFilters?.group && 
                        <>
                            <IonItem lines="none">
                                <IonLabel>Group</IonLabel>
                                <IonIcon
                                    icon={addCircle}
                                    slot="end"
                                    onClick={(e) => setShowSelectGroupModal(true)}
                                />
                            </IonItem>
                            <IonItem>
                                <IonGrid>
                                    <IonRow onClick={(e) => setShowSelectGroupModal(true)}>
                                        {selectedGroup.length > 0 &&
                                            selectedGroup.map((item, index) => (
                                                <IonCol key={index}>
                                                    <IonChip outline={true} color="primary">
                                                        <IonLabel>{groupOptions.find(g => g.id === item)?.description}</IonLabel>
                                                        <IonIcon
                                                            icon={closeCircle}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                removeGroup(item)
                                                            }}
                                                        />
                                                    </IonChip>
                                                </IonCol>
                                            ))}
                                        {selectedGroup.length === 0 && (
                                            <IonCol>
                                                <IonLabel class="secondary-text">
                                                    Select Group
                                                </IonLabel>
                                            </IonCol>
                                        )}
                                    </IonRow>
                                </IonGrid>
                            </IonItem>
                        </>
                    }

                    {
                        !hideFilters?.type && 
                        <>
                            <IonItem lines="none">
                                <IonLabel>Activity Type</IonLabel>
                                <IonIcon
                                    icon={addCircle}
                                    slot="end"
                                    onClick={(e) => setShowSelectActivityModal(true)}
                                />
                            </IonItem>
                            <IonItem>
                                <IonGrid>
                                    <IonRow onClick={(e) => setShowSelectActivityModal(true)}>
                                        {selectedActivity.length > 0 &&
                                            selectedActivity.map((item, index) => (
                                                <IonCol key={index}>
                                                    <IonChip outline={true} color="primary">
                                                        <IonLabel>{acitivitesOptions.find(a => a.id === item)?.description}</IonLabel>
                                                        <IonIcon
                                                            icon={closeCircle}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                removeActivity(item)
                                                            }}
                                                        />
                                                    </IonChip>
                                                </IonCol>
                                            ))}
                                        {selectedActivity.length === 0 && (
                                            <IonCol>
                                                <IonLabel class="secondary-text">
                                                    Select Activity Type
                                                </IonLabel>
                                            </IonCol>
                                        )}
                                    </IonRow>
                                </IonGrid>
                            </IonItem>
                        </>
                    }

                     {/* Serviced by filter */}
                     <IonItem
                        onClick={() => setShowAgentUserModal(true)}
                        lines="none"
                        style={{}}
                    >
                        <IonLabel position="stacked">Serviced by</IonLabel>
                        <IonIcon icon={addCircle} slot="end" />
                    </IonItem>

                    <IonItem>
                        <IonGrid>
                            <IonRow>
                                {servicedByAgents.length > 0 ? (
                                    servicedByAgents.map((agent) => (
                                        <IonCol
                                            key={agent.agentId}
                                            sizeSm="6"
                                            sizeMd="4"
                                            sizeLg="3"
                                        >
                                            <IonChip outline color="primary">
                                                <IonLabel>{agent.agentName}</IonLabel>
                                                <IonIcon
                                                    icon={closeCircle}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        removeServicedByAgent(agent.agentId);
                                                    }}
                                                    style={{
                                                        fontSize: "16px",
                                                    }}
                                                />
                                            </IonChip>
                                        </IonCol>
                                    ))
                                ) : (
                                    <IonCol className="">
                                        <IonLabel class="secondary-text">
                                            No agents selected
                                        </IonLabel>
                                    </IonCol>
                                )}
                            </IonRow>
                        </IonGrid>
                    </IonItem>

                    { !hideFilters?.location &&
                        <>
                            <IonItem lines="none">
                                <IonLabel>State</IonLabel>
                            </IonItem>
                            <IonItem>
                                <IonGrid>
                                    <IonRow onClick={() => setShowSelectStateModal(true)}>
                                        {selectedState ? (
                                            <IonCol>
                                                <IonChip outline={true} color="primary">
                                                    <IonLabel>{selectedState}</IonLabel>
                                                    <IonIcon
                                                        icon={closeCircle}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDropdownChange('state', '');
                                                        }}
                                                    />
                                                </IonChip>
                                            </IonCol>
                                        ) : (
                                            <IonCol>
                                                <IonLabel class="secondary-text">
                                                    Select State
                                                </IonLabel>
                                            </IonCol>
                                        )}
                                    </IonRow>
                                </IonGrid>
                            </IonItem>

                            <IonItem lines="none">
                                <IonLabel>District</IonLabel>
                            </IonItem>
                            <IonItem className={!selectedState ? 'disabled' : ''}>
                                <IonGrid>
                                    <IonRow onClick={() => selectedState && !districts.length ? loadDistricts(selectedState) : setShowSelectDistrictModal(true)}>
                                        {selectedDistrict ? (
                                            <IonCol>
                                                <IonChip outline={true} color="primary">
                                                    <IonLabel>{selectedDistrict}</IonLabel>
                                                    <IonIcon
                                                        icon={closeCircle}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDropdownChange('district', '');
                                                        }}
                                                    />
                                                </IonChip>
                                            </IonCol>
                                        ) : (
                                            <IonCol>
                                                <IonLabel class="secondary-text">
                                                    {!selectedState ? 'Select State First' : isDistrictLoading ? 'Loading...' : 'Select District'}
                                                </IonLabel>
                                            </IonCol>
                                        )}
                                    </IonRow>
                                </IonGrid>
                            </IonItem>

                            <IonItem lines="none">
                                <IonLabel>Sub District</IonLabel>
                            </IonItem>
                            <IonItem className={!selectedDistrict ? 'disabled' : ''}>
                                <IonGrid>
                                    <IonRow onClick={() => selectedDistrict && !subDistricts.length ? loadSubDistricts(selectedDistrict) : setShowSelectSubDistrictModal(true)}>
                                        {selectedSubDistrict ? (
                                            <IonCol>
                                                <IonChip outline={true} color="primary">
                                                    <IonLabel>{selectedSubDistrict}</IonLabel>
                                                    <IonIcon
                                                        icon={closeCircle}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDropdownChange('subDistrict', '');
                                                        }}
                                                    />
                                                </IonChip>
                                            </IonCol>
                                        ) : (
                                            <IonCol>
                                                <IonLabel class="secondary-text">
                                                    {!selectedDistrict ? 'Select District First' : isSubDistrictLoading ? 'Loading...' : 'Select Sub District'}
                                                </IonLabel>
                                            </IonCol>
                                        )}
                                    </IonRow>
                                </IonGrid>
                            </IonItem>


                        </>
                    }
                </IonList>
                <IonGrid>
                    <IonRow>
                        <IonCol><IonButton color="danger" className="shiftRight" onClick={onReset}>Reset</IonButton></IonCol>
                        <IonCol><IonButton onClick={onDone}>Done</IonButton></IonCol>
                    </IonRow>
                </IonGrid>

                {showSelectGroupModal && (
                    <IonModal isOpen={showSelectGroupModal}>
                        <SelectItem
                            label="Select Group"
                            items={groupOptions.filter(
                                (item: any) =>
                                    !selectedGroup.includes(item.id)
                            )}
                            labelKey="description"
                            valueKey="id"
                            setItem={addGroup}
                            selectedValue=""
                            onClose={setShowSelectGroupModal}
                        />
                    </IonModal>
                )}

                {showSelectActivityModal && (
                    <IonModal isOpen={showSelectActivityModal}>
                        <SelectItem
                            label="Select Activity Type"
                            items={acitivitesOptions.filter(
                                (item: any) =>
                                    !selectedActivity.includes(item.id)
                            )}
                            labelKey="description"
                            valueKey="id"
                            setItem={addActivity}
                            selectedValue=""
                            onClose={setShowSelectActivityModal}
                        />
                    </IonModal>
                )}

                {showSelectStateModal && (
                    <IonModal isOpen={showSelectStateModal}>
                        <SelectItem
                            label="Select State"
                            items={states}
                            labelKey="label"
                            valueKey="value"
                            setItem={setState}
                            selectedValue={selectedState}
                            onClose={setShowSelectStateModal}
                        />
                    </IonModal>
                )}

                {showSelectDistrictModal && (
                    <IonModal isOpen={showSelectDistrictModal}>
                        <SelectItem
                            label="Select District"
                            items={districts}
                            labelKey="label"
                            valueKey="value"
                            setItem={setDistrict}
                            selectedValue={selectedDistrict}
                            onClose={setShowSelectDistrictModal}
                        />
                    </IonModal>
                )}

                {showSelectSubDistrictModal && (
                    <IonModal isOpen={showSelectSubDistrictModal}>
                        <SelectItem
                            label="Select Sub District"
                            items={subDistricts}
                            labelKey="label"
                            valueKey="value"
                            setItem={setSubDistrict}
                            selectedValue={selectedSubDistrict}
                            onClose={setShowSelectSubDistrictModal}
                        />
                    </IonModal>
                )}

                {/* Agent Selection Modal */}
                <IonModal isOpen={showAgentUserModal}>
                    <IonHeader>
                        <IonToolbar>
                            <IonButtons slot="end">
                                <IonButton onClick={() => setShowAgentUserModal(false)}>
                                    Close
                                </IonButton>
                            </IonButtons>
                            <IonTitle>Select Agents</IonTitle>
                        </IonToolbar>
                        <IonToolbar>
                            <IonSearchbar
                                placeholder="Search by name or mobile..."
                                value={searchTerm}
                                onIonChange={(e) => setSearchTerm(e.detail.value!)}
                            />
                        </IonToolbar>
                    </IonHeader>

                    <IonContent>
                        {agentsList
                            ?.filter((agent: Agent) =>
                                searchTerm
                                    ? `${agent.username} ${agent.mobile}`
                                        .toLowerCase()
                                        .includes(searchTerm.toLowerCase())
                                    : true
                            )
                            .map((agent: Agent) => (
                                <IonCard
                                    key={agent.farmUserId}
                                    onClick={() => {
                                        handleAgentSelection(agent);
                                        setShowAgentUserModal(false);
                                    }}
                                >
                                    <IonItem lines="none">
                                        <IonLabel>
                                            <h2>Name: {agent.username}</h2>
                                            <p>Mobile: {agent.mobile || "No Mobile Number"}</p>
                                        </IonLabel>
                                    </IonItem>
                                </IonCard>
                            ))}
                    </IonContent>
                </IonModal>


            </IonContent>
        </IonPage>
    );
};

export default SalesActivityFilter;
